import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  IPCollectionsApiProvider,
  useIPCollectionsApi,
} from '../Services/IPCollectionsApiProvider';

import { IPCollectionApi } from '../Services/IPCollectionsApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

export const ipCollectionsColumns = [
  {
    name: 'ID',
    selector: (row: IPCollectionApi) => row.id,
    sortable: false,
  },
  {
    name: 'Name',
    selector: (row: IPCollectionApi) => row.name,
    sortable: true,
  },
  {
    name: 'Status',
    selector: (row: IPCollectionApi) => (row.is_active ? 'Active' : 'Inactive'),
    sortable: false,
  },
  {
    name: 'Created',
    selector: (row: IPCollectionApi) => formatDateUTC(row.created_at),
    sortable: true,
  },
  {
    name: 'Updated',
    selector: (row: IPCollectionApi) => formatDateUTC(row.updated_at),

    sortable: true,
  },
];

function CollectionsContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getIPCollections, deleteIPCollection } = useIPCollectionsApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);
  const [ipCollections, setIpCollections] = useState<IPCollectionApi[]>([]);
  const [totalIpCollections, setTotalIpCollections] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  function onDelete(selectedId: string[]): void {
    if (!selectedId[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        await deleteIPCollection(selectedId[0]);
        showAlert('success', 'Item was successfully deleted!');
        fetchIpCollections(0, itemsPerPage);
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  }

  function onOpen(selectedId: string): void {
    if (!selectedId) return;
    navigate(`/organizations/${organizationId}/information-package-collections/${selectedId}`, {
      state: { organizationId },
    });
  }

  const fetchIpCollections = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response = await getIPCollections(skip, limit);
      setIpCollections(response.items);
      setTotalIpCollections(response.totalCount);
    } catch (error) {
      setError(error.message || 'Failed to fetch IP collections!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIpCollections(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="IP Collections"
                columns={ipCollectionsColumns}
                data={ipCollections}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                paginationTotalRows={totalIpCollections}
                onRowClicked={(row) => onOpen(row.id)}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={onDelete}
                onChangePage={fetchIpCollections}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/information-package-collections/new-ip-collection`,
                    )
                  }
                >
                  Create New IP Collection
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InformationPackageCollections({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <IPCollectionsApiProvider>
        <CollectionsContent canEdit={canEdit} />
      </IPCollectionsApiProvider>
    </ConfirmationProvider>
  );
}

export default InformationPackageCollections;
